import React, {useEffect, useRef} from "react";

import classes from "./HeroPhoneAnimation.module.scss";
import {HERO_PHONE_DASHBOARD_OVERLAY, HERO_PHONE_SLIDES} from "./heroPhoneSlides";

interface HeroPhoneAnimationProps {
  showFloatingElements?: boolean;
  variant?: "default" | "light";
}

export function HeroPhoneAnimation({showFloatingElements = false, variant = "default"}: HeroPhoneAnimationProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageClassName = [
    classes.phoneStage,
    showFloatingElements ? classes.phoneStageWithFloatingElements : "",
    variant === "light" ? classes.phoneStageLight : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }

    const slides = Array.from(root.querySelectorAll<HTMLElement>("[data-phone-slide]"));
    const tap = root.querySelector<HTMLElement>("[data-phone-tap]");
    const bottomOverlay = root.querySelector<HTMLElement>("[data-phone-overlay]");

    if (!slides.length || !tap || !bottomOverlay) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      return;
    }

    const dwellMs = 1000;
    const tapBeatMs = 200;
    const pushMs = 400;
    const carouselMs = 340;
    const fadeMs = 500;
    const selectMs = 180;
    const swipeGestureMs = 560;
    const timers: number[] = [];
    const frames: number[] = [];
    const transitionClasses = [
      classes.phoneSlidePushIn,
      classes.phoneSlidePushOut,
      classes.phoneSlideCarouselIn,
      classes.phoneSlideCarouselOut,
      classes.phoneSlideFadeIn,
      classes.phoneSlideFadeOut,
      classes.phoneSlideSelectIn,
      classes.phoneSlideSelectOut,
    ];
    let current = 0;
    let disposed = false;

    const schedule = (callback: () => void, delay: number) => {
      const timer = window.setTimeout(() => {
        if (!disposed) {
          callback();
        }
      }, delay);
      timers.push(timer);

      return timer;
    };

    const clearTransitionClasses = (slide: HTMLElement) => {
      slide.classList.remove(...transitionClasses);
    };

    const positionTap = (slide: HTMLElement) => {
      tap.style.left = `${slide.dataset.tapX || "50"}%`;
      tap.style.top = `${slide.dataset.tapY || "90"}%`;
    };

    const showTap = (slide: HTMLElement) => {
      positionTap(slide);
      tap.classList.remove(classes.tapIndicatorSwipe);
      tap.classList.add(classes.tapIndicatorShow);

      const frame = window.requestAnimationFrame(() => {
        if (!disposed) {
          schedule(() => tap.classList.add(classes.tapIndicatorTap), 40);
        }
      });
      frames.push(frame);
    };

    const showSwipe = (slide: HTMLElement) => {
      tap.classList.remove(classes.tapIndicatorShow, classes.tapIndicatorTap);
      tap.style.setProperty("--swipe-y", `${slide.dataset.swipeY || "30"}%`);
      tap.classList.remove(classes.tapIndicatorSwipe);
      void tap.offsetWidth;
      tap.classList.add(classes.tapIndicatorSwipe);
    };

    const hideTap = () => {
      tap.classList.remove(classes.tapIndicatorShow, classes.tapIndicatorTap, classes.tapIndicatorSwipe);
    };

    const runPush = (outEl: HTMLElement, inEl: HTMLElement) => {
      outEl.classList.remove(classes.phoneSlideActive);
      outEl.classList.add(classes.phoneSlidePushOut);
      clearTransitionClasses(inEl);
      inEl.classList.add(classes.phoneSlidePushIn, classes.phoneSlideActive);
      schedule(() => {
        outEl.classList.remove(classes.phoneSlidePushOut);
        inEl.classList.remove(classes.phoneSlidePushIn);
      }, pushMs + 40);
    };

    const runCarousel = (outEl: HTMLElement, inEl: HTMLElement) => {
      bottomOverlay.classList.add(classes.dashboardBottomOverlayOn);
      outEl.classList.remove(classes.phoneSlideActive);
      outEl.classList.add(classes.phoneSlideCarouselOut);
      clearTransitionClasses(inEl);
      inEl.classList.add(classes.phoneSlideCarouselIn, classes.phoneSlideActive);
      schedule(() => {
        outEl.classList.remove(classes.phoneSlideCarouselOut);
        inEl.classList.remove(classes.phoneSlideCarouselIn);
        schedule(() => bottomOverlay.classList.remove(classes.dashboardBottomOverlayOn), 160);
      }, carouselMs + 40);
    };

    const runFade = (outEl: HTMLElement, inEl: HTMLElement) => {
      outEl.classList.remove(classes.phoneSlideActive);
      outEl.classList.add(classes.phoneSlideFadeOut);
      clearTransitionClasses(inEl);
      inEl.style.transform = "translateX(0)";
      inEl.classList.add(classes.phoneSlideFadeIn, classes.phoneSlideActive);
      schedule(() => {
        outEl.classList.remove(classes.phoneSlideFadeOut);
        inEl.classList.remove(classes.phoneSlideFadeIn);
        inEl.style.transform = "";
      }, fadeMs + 40);
    };

    const runSelect = (outEl: HTMLElement, inEl: HTMLElement) => {
      outEl.classList.remove(classes.phoneSlideActive);
      outEl.classList.add(classes.phoneSlideSelectOut);
      clearTransitionClasses(inEl);
      inEl.style.transform = "translateX(0)";
      inEl.classList.add(classes.phoneSlideSelectIn, classes.phoneSlideActive);
      schedule(() => {
        outEl.classList.remove(classes.phoneSlideSelectOut);
        inEl.classList.remove(classes.phoneSlideSelectIn);
        inEl.style.transform = "";
      }, selectMs + 40);
    };

    const advance = () => {
      const currentEl = slides[current];
      const nextIndex = (current + 1) % slides.length;
      const nextEl = slides[nextIndex];
      const transition = currentEl.dataset.transOut || "push";

      if (transition === "carousel") {
        showSwipe(currentEl);
        schedule(() => {
          hideTap();
          runCarousel(currentEl, nextEl);
        }, swipeGestureMs - 60);
      } else if (transition === "fade") {
        showTap(currentEl);
        schedule(() => {
          hideTap();
          runFade(currentEl, nextEl);
        }, tapBeatMs);
      } else if (transition === "select") {
        showTap(currentEl);
        schedule(() => {
          hideTap();
          runSelect(currentEl, nextEl);
        }, tapBeatMs);
      } else {
        showTap(currentEl);
        schedule(() => {
          hideTap();
          runPush(currentEl, nextEl);
        }, tapBeatMs);
      }

      current = nextIndex;
    };

    const nextDelay = () => {
      const transition = slides[current].dataset.transOut || "push";

      if (transition === "carousel") {
        return dwellMs + swipeGestureMs + carouselMs;
      }
      if (transition === "fade") {
        return dwellMs + tapBeatMs + fadeMs;
      }
      if (transition === "select") {
        return dwellMs + tapBeatMs + selectMs;
      }

      return dwellMs + tapBeatMs + pushMs;
    };

    schedule(function loop() {
      advance();
      schedule(loop, nextDelay());
    }, dwellMs);

    return () => {
      disposed = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      frames.forEach((frame) => window.cancelAnimationFrame(frame));
    };
  }, []);

  return (
    <div className={stageClassName} data-hero-phone ref={rootRef} aria-hidden="true">
      {showFloatingElements ? (
        <div className={classes.phoneFloatingElements}>
          <div className={`${classes.phoneFloatCard} ${classes.phoneFloatCardTax}`}>
            <span>Estimated taxes</span>
            <strong>$6,230</strong>
            <em>Q2 ready</em>
          </div>
          <div className={`${classes.phoneFloatCard} ${classes.phoneFloatCardRetirement}`}>
            <span>Retirement plan</span>
            <strong>On track</strong>
            <em>Base account active</em>
          </div>
          <div className={`${classes.phoneFloatCard} ${classes.phoneFloatCardTasks}`}>
            <span>Open items</span>
            <strong>4 tasks</strong>
            <em>Reviewed by your team</em>
          </div>
        </div>
      ) : null}
      <div className={classes.phoneDevice}>
        <div className={classes.phoneScreen}>
          <div className={classes.phoneIsland} />
          <div className={classes.phoneSlidesStack}>
            {HERO_PHONE_SLIDES.map((slide, index) => (
              <div
                className={`${classes.phoneSlide} ${index === 0 ? classes.phoneSlideActive : ""}`}
                data-phone-slide
                data-trans-out={slide.transition}
                data-tap-x={slide.tapX}
                data-tap-y={slide.tapY}
                data-swipe-y={slide.swipeY}
                key={slide.src}>
                <img src={slide.src} alt="" decoding="async" />
              </div>
            ))}
          </div>
          <div
            className={classes.dashboardBottomOverlay}
            data-phone-overlay
            style={{backgroundImage: `url("${HERO_PHONE_DASHBOARD_OVERLAY}")`}}
          />
          <div className={classes.tapIndicator} data-phone-tap>
            <div className={classes.tapIndicatorRipple} />
            <div className={classes.tapIndicatorDot} />
          </div>
        </div>
      </div>
      <div className={classes.phoneReflection} />
    </div>
  );
}
